import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';

import { selectUserAccessToken } from '../store/selectors/auth';
import * as AuthUtils from '../utils/auth';

export default function useAuthQuery<TResponse>(queryOptions: UseQueryOptions<TResponse>) {
  const firebase = useFirebase();
  const token = useSelector(selectUserAccessToken);
  const tokenRef = useRef({ token });

  useEffect(() => {
    tokenRef.current.token = token;
  }, [token]);

  const wait = useCallback((oldToken?: string, timeout = 5000) => {
    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;

    return Promise.race([
      new Promise<void>((res) => {
        intervalId = setInterval(() => {
          if (typeof oldToken !== 'undefined' && oldToken !== tokenRef.current.token) {
            res();
          }
        }, 50);
      }),
      new Promise((res, rej) => {
        timeoutId = setTimeout(() => {
          rej();
        }, timeout);
      }),
    ])
      .catch(() => console.warn('Authentication error'))
      .finally(() => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      });
  }, []);

  const { ...results } = useQuery<TResponse>(queryOptions);
  const { error, errorUpdateCount, refetch } = results;

  useEffect(() => {
    const oldToken = tokenRef.current.token;

    (async () => {
      if ((error as unknown as Response)?.status === 401) {
        if (errorUpdateCount === 1) {
          await AuthUtils.getIdToken(true)
            ?.then(firebase.reloadAuth)
            .then(() => wait(oldToken))
            // tslint:disable-next-line:no-unnecessary-callback-wrapper
            .then(() => refetch())
            .catch(() => console.warn('Something went wrong with reloading auth.'));
        } else {
          await firebase.logout();
        }
      }
    })();
  }, [error, errorUpdateCount, firebase, refetch, wait]);

  return results;
}
